import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { bool, func, objectOf, shape, string } from "prop-types";

import { RecoveryUserpassStep2 } from "@ui-kit/components/index.es";
import { actions as layoutActions } from "reducers/layout";
import { actions as recoveryPasswordActions, selectors as recoveryPasswordSelectors } from "reducers/recoveryPassword";
import * as i18n from "util/i18n";
import Yup from "yup";

const FORM_ID = "recoveryPassword.step2";

function RecoveryUserpassStep2V3({ dispatch, history, isRecoveryPassAndUserpass, recoveryAsyncPending, fieldErrors }) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["recoveryUserpass"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
        };
    }, [dispatch]);

    const initialValues = useMemo(
        () => ({
            resetCode: "",
        }),
        [],
    );

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                resetCode: Yup.string().required(i18n.get(`${FORM_ID}.resetCode.required`)),
            }),
        [],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            const code = values.resetCode ?? "";

            const enhancedBag = {
                ...formikBag,
                props: { ...(formikBag.props || {}), location: { pathname: "/v3/recoveryUserpass/step2" }, history },
            };

            if (isRecoveryPassAndUserpass) {
                dispatch(recoveryPasswordActions.recoveryPassAndUserpassStep1(code, enhancedBag));
            } else {
                dispatch(recoveryPasswordActions.recoveryUserpassStep2(code, enhancedBag));
            }
        },
        [dispatch, isRecoveryPassAndUserpass],
    );

    const handleResendCode = useCallback(() => {
        history.push("/v3/recoveryUserpass/step1");
    }, [history]);

    const getLabels = useCallback((id) => {
        const keys = {
            resetCode: `${FORM_ID}.resetCode.label`,
            continue: "global.continue",
            resendCode: "recoveryPassword.step2.resendMail.link",
        };
        return i18n.get(keys[id] ?? id);
    }, []);

    const getText = useCallback(
        (id) => {
            const keys = {
                "recoveryUserpass.step2.header": "recoveryPassword.step2.header",
                "recoveryUserpass.step2.message": isRecoveryPassAndUserpass
                    ? "recoveryPassAndUserpass.v3.step2.mailSent.message"
                    : "recoveryUserpass.v3.step2.mailSent.message",
            };
            return i18n.get(keys[id] ?? id);
        },
        [isRecoveryPassAndUserpass],
    );

    return (
        <RecoveryUserpassStep2
            initialValues={initialValues}
            validationSchema={validationSchema}
            handleSubmit={handleSubmit}
            handleResendCode={handleResendCode}
            getLabels={getLabels}
            getText={getText}
            submitLoading={recoveryAsyncPending}
            resendLoading={false}
            fieldErrors={fieldErrors ?? undefined}
        />
    );
}

RecoveryUserpassStep2V3.propTypes = {
    dispatch: func.isRequired,
    history: shape({ push: func }).isRequired,
    isRecoveryPassAndUserpass: bool,
    recoveryAsyncPending: bool,
    fieldErrors: objectOf(string),
};

RecoveryUserpassStep2V3.defaultProps = {
    isRecoveryPassAndUserpass: false,
    recoveryAsyncPending: false,
    fieldErrors: undefined,
};

const mapStateToProps = (state) => ({
    isRecoveryPassAndUserpass: recoveryPasswordSelectors.getRecoveryPassAndUserpass(state),
    recoveryAsyncPending: recoveryPasswordSelectors.getAsyncPending(state),
    fieldErrors: recoveryPasswordSelectors.getFormFieldErrors(state) ?? undefined,
});

export default connect(mapStateToProps)(RecoveryUserpassStep2V3);
